import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { 
  BarChart3, TrendingUp, Users, Activity,
  Search, ChevronLeft, ChevronRight, Eye,
  RefreshCw, LogOut, Download, Loader2,
  AlertTriangle, Clock, CheckCircle2, Mail, Calendar,
  XCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, trend, className = "" }) => (
  <Card className={className} data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      <Icon className="h-4 w-4 text-muted-foreground" />
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold text-[#1C2470]">{value}</div>
      {trend && (
        <p className="text-xs text-muted-foreground mt-1">{trend}</p>
      )}
    </CardContent>
  </Card>
);

// CH-55: Enhanced Status Badge Component with colors
const StatusBadge = ({ status }) => {
  const config = {
    completed: { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle2 },
    complete: { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle2 },
    partial: { bg: "bg-amber-100", text: "text-amber-700", icon: AlertTriangle },
    processing: { bg: "bg-blue-100", text: "text-blue-700", icon: Loader2, animate: true },
    failed: { bg: "bg-red-100", text: "text-red-700", icon: XCircle },
    pending: { bg: "bg-yellow-100", text: "text-yellow-700", icon: Clock }
  };
  const cfg = config[status] || { bg: "bg-gray-100", text: "text-gray-700", icon: Clock };
  const IconComponent = cfg.icon;
  
  return (
    <Badge className={`${cfg.bg} ${cfg.text} flex items-center gap-1`}>
      <IconComponent className={`h-3 w-3 ${cfg.animate ? 'animate-spin' : ''}`} />
      {status}
    </Badge>
  );
};

// Score Badge Component
const ScoreBadge = ({ score }) => {
  const getColor = () => {
    if (score >= 80) return "bg-green-100 text-green-700";
    if (score >= 65) return "bg-green-50 text-green-600";
    if (score >= 45) return "bg-amber-100 text-amber-700";
    return "bg-red-100 text-red-700";
  };
  return <Badge className={getColor()}>{score}/100</Badge>;
};

// CH-55: Failure Tracking Widget
const FailureTrackingWidget = ({ failureTracking }) => {
  if (!failureTracking || !failureTracking.stageBreakdown?.length) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Failure Tracking
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold text-green-600">0%</p>
          <p className="text-xs text-muted-foreground">No failures recorded</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="col-span-2" data-testid="failure-tracking-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          CH-55: Reliability by Stage
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {failureTracking.stageBreakdown.map((item) => (
            <div key={item.stage} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium capitalize">{item.stage.replace(/_/g, ' ')}</span>
                <span className="text-muted-foreground">{item.count} failures ({item.percentage}%)</span>
              </div>
              <Progress value={item.percentage} className="h-2" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// CH-55: Funnel Widget (Audits → Emails → Calendly)
const FunnelWidget = ({ funnel }) => {
  const audits = funnel?.auditsRun || 0;
  const emails = funnel?.emailsSent || 0;
  const calendly = funnel?.calendlyClicks || 0;
  
  return (
    <Card data-testid="funnel-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          CH-55: Conversion Funnel
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-[#1C2470]">{audits}</p>
            <p className="text-xs text-muted-foreground">Audits Run</p>
          </div>
          <div className="text-muted-foreground">→</div>
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600">{emails}</p>
            <p className="text-xs text-muted-foreground">Emails Sent</p>
          </div>
          <div className="text-muted-foreground">→</div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{calendly}</p>
            <p className="text-xs text-muted-foreground">Calendly Clicks</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// CH-55: Duration formatting
const formatDuration = (ms) => {
  if (!ms) return "-";
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

// Download file from blob utility
const downloadBlobAsFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [identityVerified, setIdentityVerified] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: "", status: "all" });

  // Set page title
  useEffect(() => {
    document.title = "DHI | Admin Command Center";
  }, []);

  // Get auth token
  const getToken = () => localStorage.getItem("adminToken");

  // Verify identity before loading any data
  useEffect(() => {
    const verifyIdentity = async () => {
      const token = getToken();
      if (!token) {
        navigate("/admin/login");
        return;
      }

      try {
        const response = await axios.get(`${API}/admin/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserInfo(response.data.user);
        setIdentityVerified(true);
      } catch (error) {
        console.error("Identity verification failed:", error);
        localStorage.removeItem("adminToken");
        navigate("/admin/login");
      }
    };

    verifyIdentity();
  }, [navigate]);

  // Fetch stats (only after identity verified)
  const fetchStats = useCallback(async () => {
    if (!identityVerified) return;
    
    try {
      const token = getToken();
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      if (error.response?.status === 401) {
        navigate("/admin/login");
      }
    }
  }, [navigate, identityVerified]);

  // Fetch audits (only after identity verified)
  const fetchAudits = useCallback(async () => {
    if (!identityVerified) return;
    
    try {
      const token = getToken();
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(filters.search && { search: filters.search }),
        ...(filters.status !== "all" && { status: filters.status })
      });
      
      const response = await axios.get(`${API}/admin/audits?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAudits(response.data.data);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      }));
    } catch (error) {
      console.error("Failed to fetch audits:", error);
      if (error.response?.status === 401) {
        navigate("/admin/login");
      }
    }
  }, [pagination.page, pagination.limit, filters, navigate, identityVerified]);

  // Load data only after identity is verified
  useEffect(() => {
    if (!identityVerified) return;
    
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchAudits()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStats, fetchAudits, identityVerified]);

  // Handlers
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleStatusFilter = (value) => {
    setFilters(prev => ({ ...prev, status: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  const handleRefresh = () => {
    fetchStats();
    fetchAudits();
  };

  // Show loading state while verifying identity
  if (!identityVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#1C2470] mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying identity...</p>
        </div>
      </div>
    );
  }

  // Export to CSV handler - calls backend endpoint for server-side CSV generation
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const token = getToken();
      
      // Build query params for filtering
      const params = new URLSearchParams();
      if (filters.search) params.append("search", filters.search);
      if (filters.status && filters.status !== "all") params.append("status", filters.status);
      
      // Call backend export endpoint with blob response type
      const response = await axios.get(`${API}/admin/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"  // Important: get response as blob
      });
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers["content-disposition"];
      let filename = "dhi-audits.csv";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      
      // Trigger download using the blob
      downloadBlobAsFile(response.data, filename);
      
      toast.success("CSV downloaded successfully", {
        description: "Check your Downloads folder"
      });
    } catch (error) {
      console.error("Failed to export:", error);
      toast.error("Export failed", {
        description: error.response?.data?.message || error.message
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header - Dark Navy (#1C2470) */}
      <header className="bg-[#1C2470] text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
              dhi Hospitality Admin
            </h1>
            <p className="text-sm text-white/70">Website Audit Dashboard</p>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleRefresh}
              className="text-white hover:bg-white/10"
              data-testid="refresh-btn"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="text-white hover:bg-white/10"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {/* Stats Grid - CH-55 Enhanced */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Audits"
            value={stats?.volume?.total || 0}
            icon={BarChart3}
            trend={`${stats?.volume?.today || 0} today`}
          />
          <StatCard
            title="Average Score"
            value={stats?.performance?.averageScore?.toFixed(1) || "N/A"}
            icon={TrendingUp}
            trend={`${stats?.performance?.completionRate || 0}% completion rate`}
          />
          <StatCard
            title="Failure Rate"
            value={`${stats?.performance?.failureRate || 0}%`}
            icon={AlertTriangle}
            trend={`${stats?.byStatus?.failed || 0} failed audits`}
            className={stats?.performance?.failureRate > 10 ? "border-red-200" : ""}
          />
          <StatCard
            title="Avg Duration"
            value={formatDuration(stats?.processingDuration?.avgMs)}
            icon={Clock}
            trend={`Max: ${formatDuration(stats?.processingDuration?.maxMs)}`}
          />
        </div>
        
        {/* CH-55: Funnel & Failure Tracking Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <FunnelWidget funnel={stats?.funnel} />
          <FailureTrackingWidget failureTracking={stats?.failureTracking} />
        </div>

        {/* Audits Table Card */}
        <Card>
          <CardHeader className="border-b">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <CardTitle className="text-lg text-[#1C2470]">Recent Audits</CardTitle>
              <div className="flex items-center gap-4 flex-wrap">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by email or URL..."
                    className="pl-9 w-64"
                    value={filters.search}
                    onChange={(e) => handleSearch(e.target.value)}
                    data-testid="search-input"
                  />
                </div>
                
                {/* Status Filter - CH-55: Enhanced statuses */}
                <Select value={filters.status} onValueChange={handleStatusFilter}>
                  <SelectTrigger className="w-32" data-testid="status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Export CSV Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportCSV}
                  disabled={exporting || audits.length === 0}
                  className="border-[#C5A059] text-[#1C2470] hover:bg-[#C5A059]/10"
                  data-testid="export-csv-btn"
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Export CSV
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead>Date</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : audits.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No audits found
                    </TableCell>
                  </TableRow>
                ) : (
                  audits.map((audit) => (
                    <TableRow 
                      key={audit.id} 
                      data-testid={`audit-row-${audit.id}`}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => navigate(`/admin/audits/${audit.id}`)}
                    >
                      <TableCell className="text-sm">
                        {new Date(audit.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="font-medium">{audit.email}</TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                        {audit.url}
                      </TableCell>
                      <TableCell>
                        {audit.overall_score !== null && audit.overall_score !== undefined ? (
                          <ScoreBadge score={audit.overall_score} />
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={audit.status} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDuration(audit.processing_duration_ms || audit.processing_ms)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/audits/${audit.id}`);
                          }}
                          data-testid={`view-audit-${audit.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    data-testid="next-page-btn"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminDashboard;
